import React, { useState } from 'react';
import { MessageCircle, Zap, CheckCircle, Star, Gift, Users, ArrowRight, Sparkles, FileText, Camera, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { generateWhatsAppLink } from '../utils/whatsappTemplates';
import { CONTACT_INFO } from '../data/mockData';

const Launch = () => {
  const whatsappLink = generateWhatsAppLink('generic');
  
  const useCases = [
    {
      title: 'Student Mini-Move',
      subtitle: 'Res to res, fast & affordable',
      icon: Users,
      message: 'student move'
    },
    {
      title: 'Marketplace Pickup',
      subtitle: 'Facebook, Gumtree collections',
      icon: Gift,
      message: 'marketplace pickup'
    },
    {
      title: 'Appliance Delivery',
      subtitle: 'Fridge, washing machine, couch',
      icon: Zap,
      message: 'appliance delivery'
    },
    {
      title: 'Storage Unit Run',
      subtitle: 'Quick storage moves',
      icon: CheckCircle,
      message: 'storage unit'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#039fff] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#fb6402] rounded-full blur-3xl animate-pulse"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center bg-[#fb6402]/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <Sparkles className="h-5 w-5 text-[#fb6402] mr-2" />
              <span className="text-sm font-semibold text-[#fb6402]">Founding Customer Special</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight">
              Fast & Reliable Mini Moves and Deliveries
            </h1>
            <p className="text-2xl md:text-3xl mb-3 text-[#039fff] font-semibold">
              Stellenbosch & Cape Town | Fast • Reliable
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              Move it. Drop it. Done.
            </h2>
            <p className="text-sm md:text-base text-gray-300 mb-8 flex items-center justify-center flex-wrap gap-4">
              <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-[#039fff]" /> Quote in minutes</span>
              <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-[#039fff]" /> Same-day options</span>
              <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-2 text-[#039fff]" /> Proof-of-delivery</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8 py-6 text-lg w-full sm:w-auto shadow-xl">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WhatsApp for Quote
                </Button>
              </a>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-[#001A47] px-8 py-6 text-lg w-full sm:w-auto"
                onClick={() => document.getElementById('offer')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View Launch Offer
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* LAUNCH OFFER */}
      <section id="offer" className="py-16 md:py-20 bg-gradient-to-br from-[#fb6402]/5 to-[#039fff]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-[#fb6402] shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-[#fb6402] to-[#e55a02] text-white p-6 text-center">
                <Gift className="h-12 w-12 mx-auto mb-3" />
                <h2 className="text-3xl font-bold mb-2">Founding Customer Special</h2>
                <p className="text-white/90">Limited time offer for early adopters</p>
              </div>
              <CardContent className="p-8 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="text-4xl font-bold text-[#fb6402] mb-2">R100</div>
                    <div className="text-sm text-gray-600">off your first booking</div>
                  </div>
                  <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="text-4xl font-bold text-[#039fff] mb-2">FREE</div>
                    <div className="text-sm text-gray-600">helper upgrade (limited slots)</div>
                  </div>
                  <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="text-4xl font-bold text-[#001A47] mb-2">SPECIAL</div>
                    <div className="text-sm text-gray-600">student move bundle</div>
                  </div>
                </div>
                
                <p className="text-sm text-center text-gray-600 mb-6">
                  <strong>Terms:</strong> Limited to Stellenbosch + Cape Town metro. Subject to capacity. One offer per customer.
                </p>
                
                <div className="text-center">
                  <a href={generateWhatsAppLink('generic', { issue: 'I want the founding customer special' })} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-12 py-6 text-lg">
                      <MessageCircle className="mr-2 h-5 w-5" />
                      Claim Your Offer on WhatsApp
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS (Fast) */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Three simple steps to get moving</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: 1, title: 'WhatsApp us your details', desc: 'Send pickup + drop-off pins and item details', icon: MessageCircle },
              { step: 2, title: 'Get a quote', desc: 'Clear pricing in minutes', icon: FileText },
              { step: 3, title: 'We move it', desc: 'Updates + proof-of-delivery', icon: CheckCircle }
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.step} className="text-center relative">
                  {item.step < 3 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-[#039fff]/30"></div>
                  )}
                  <div className="bg-[#fb6402] text-white rounded-full w-16 h-16 flex items-center justify-center font-bold text-2xl mx-auto mb-4 relative z-10 shadow-lg">
                    {item.step}
                  </div>
                  <Icon className="h-10 w-10 text-[#039fff] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#001A47] mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">Choose Your Scenario</h2>
            <p className="text-lg text-gray-600">What do you need moved?</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              const link = generateWhatsAppLink('generic', { issue: useCase.message });
              
              return (
                <a key={index} href={link} target="_blank" rel="noopener noreferrer">
                  <Card className="hover:shadow-xl transition-all hover:scale-105 cursor-pointer h-full">
                    <CardContent className="p-6 text-center">
                      <div className="bg-[#039fff]/10 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-[#039fff]" />
                      </div>
                      <h3 className="font-bold text-[#001A47] mb-2">{useCase.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">{useCase.subtitle}</p>
                      <Button variant="outline" size="sm" className="w-full">
                        Get Quote
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST & PROOF */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">The Rocket Van Express Trust System</h2>
            <p className="text-lg text-gray-600">Why customers choose us</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: CheckCircle, title: 'Driver Verification', text: 'ID & background checks' },
              { icon: MessageCircle, title: 'Live Updates', text: 'Real-time WhatsApp tracking' },
              { icon: Camera, title: 'Proof-of-Delivery', text: 'Photo & signature confirmation' },
              { icon: Phone, title: 'Clear Support', text: 'Help when you need it' }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="h-12 w-12 text-[#039fff] mx-auto mb-3" />
                  <h3 className="font-bold text-[#001A47] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REFERRAL TEASER */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#039fff]/10 to-[#fb6402]/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Users className="h-16 w-16 text-[#fb6402] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-[#001A47] mb-4">Invite a friend. Both get rewarded.</h2>
            <p className="text-lg text-gray-600 mb-8">
              Share Rocket Van Express with friends and both of you get special discounts on your next move.
            </p>
            <a href={generateWhatsAppLink('referral')} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#039fff] hover:bg-[#0288e0] text-white px-8 py-6">
                <Gift className="mr-2 h-5 w-5" />
                Get Referral Code on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready? Get your quote in minutes.</h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Fast, reliable, and trusted by students, families, and businesses across Stellenbosch and Cape Town.
          </p>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-12 py-8 text-xl shadow-2xl w-full sm:w-auto">
              <MessageCircle className="mr-3 h-6 w-6" />
              WhatsApp for Quote
            </Button>
          </a>
          <p className="text-sm text-gray-400 mt-6">
            Call us: <a href={`tel:${CONTACT_INFO.phone}`} className="text-[#039fff] hover:underline">{CONTACT_INFO.phone}</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Launch;
